import React from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  CalendarIcon,
  TrashIcon,
  EyeIcon,
  ClockIcon,
  UsersIcon,
} from '@heroicons/react/24/solid';
import { formatDisplayDate } from '../../utils/timeUtils';

const SessionRow = ({
  session,
  isChild = false,
  parentSessionPresent = false,
  onToggleExpand,
  isExpanded,
  handleViewDetails,
  handleEdit,
  handleSchedule,
  handleDelete,
  handleReschedule,
  handleQuickSchedule,
  formatCrewNames,
  formatSessionTiming,
  getSessionStatus,
  canModify,
}) => {
  const hasChildren = session.childSessions && session.childSessions.length > 0;
  const canExpand = hasChildren && !isChild;
  const status = getSessionStatus(session);
  const isParentSession =
    (!session.parent_session_id && hasChildren) ||
    (session.parent_session_id && parentSessionPresent);

  return (
    <>
      <tr
        className={`hover:bg-gray-50 ${isChild ? 'border-l-2 border-l-blue-200 bg-gray-50' : ''}`}
      >
        <td className="px-6 py-4 align-top">
          <div className={`flex items-center gap-2 ${isChild ? 'ml-8' : ''}`}>
            {canExpand && (
              <button
                onClick={() => onToggleExpand(session.id)}
                className="mr-2 text-gray-400 hover:text-gray-600"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </button>
            )}
            {!canExpand && isChild && (
              <div className="mr-2 flex h-4 w-6 items-center justify-center">
                <div className="h-px w-3 bg-gray-300"></div>
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <button
                  className="text-left text-sm font-medium text-gray-900 transition-colors hover:text-blue-600 whitespace-normal break-words"
                  onClick={() => handleViewDetails(session)}
                  title="View session details"
                >
                  {session.title}
                  {hasChildren && (
                    <span className="ml-2 text-xs text-gray-500">
                      ({session.childSessions.length} split
                      {session.childSessions.length !== 1 ? 's' : ''})
                    </span>
                  )}
                </button>
              </div>
              {session.description && (
                <div className="text-sm text-gray-500 whitespace-normal break-words">{session.description}</div>
              )}
            </div>
          </div>
        </td>
        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
          {isChild ? (
            ''
          ) : (
            <div className="flex items-center">
              <CalendarIcon className="mr-1 h-4 w-4 text-gray-400" />
              {formatDisplayDate(new Date(session.date))}
            </div>
          )}
          <div className="flex items-center text-gray-500">
            <ClockIcon className="mr-1 h-4 w-4" />
            {formatSessionTiming(session)}
          </div>
        </td>
        <td className="whitespace-nowrap px-6 py-4">
          <div className="flex flex-col">
            <span
              className={`mt-1 inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-medium ${status === 'completed'
                  ? 'bg-green-100 text-green-800'
                  : status === 'partially-scheduled'
                    ? 'bg-blue-100 text-blue-800'
                    : status === 'scheduled'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                }`}
            >
              {status === 'completed'
                ? 'Completed'
                : status === 'partially-scheduled'
                  ? 'Partially Scheduled'
                  : status === 'scheduled'
                    ? 'Scheduled'
                    : 'Unscheduled'}
            </span>
          </div>
        </td>
        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
          {session.location || '-'}
        </td>
        <td className="px-6 py-4 text-sm text-gray-900">
          <div className="flex items-center">
            <UsersIcon className="mr-1 h-4 w-4 text-gray-400" />
            <span>{formatCrewNames(session.crew_assignments, session)}</span>
          </div>
        </td>
        <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
          <div className="flex items-center space-x-1">
            {/* View Details button - available to all users */}
            <button
              onClick={() => handleViewDetails(session)}
              className="rounded p-1 text-green-600 hover:bg-green-50 hover:text-green-900"
              title="View details"
            >
              <EyeIcon className="h-4 w-4" />
            </button>

            {canModify && (
              <>
                {/* Parent session: only schedule, edit, delete */}
                {isParentSession && (
                  <>
                    {status !== 'completed' && (
                      <>
                        <button
                          onClick={() => handleSchedule(session)}
                          className="rounded p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-900"
                          title="Schedule session"
                        >
                          <CalendarIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleQuickSchedule(session)}
                          className="rounded p-1 text-green-600 hover:bg-green-50 hover:text-green-900"
                          title="Quick schedule session"
                        >
                          <ClockIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleEdit(session)}
                      className="rounded p-1 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-900"
                      title="Edit session"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(session)}
                      className="rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-900"
                      title="Delete session"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </>
                )}

                {/* Child session: reschedule, edit, delete */}
                {isChild && (
                  <>
                    {session.session_type === 'scheduled' && (
                      <button
                        onClick={() => handleReschedule(session)}
                        className="rounded p-1 text-yellow-600 hover:bg-yellow-50 hover:text-yellow-900"
                        title="Reschedule to unscheduled"
                      >
                        <ClockIcon className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(session)}
                      className="rounded p-1 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-900"
                      title="Edit session time"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(session)}
                      className="rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-900"
                      title="Delete session"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </>
                )}

                {/* Regular session (no parent, no children): all actions */}
                {!isParentSession && !isChild && (
                  <>
                    {session.session_type === 'unscheduled' && (
                      <>
                        <button
                          onClick={() => handleSchedule(session)}
                          className="rounded p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-900"
                          title="Schedule session"
                        >
                          <CalendarIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleQuickSchedule(session)}
                          className="rounded p-1 text-green-600 hover:bg-green-50 hover:text-green-900"
                          title="Quick schedule session"
                        >
                          <ClockIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleEdit(session)}
                      className="rounded p-1 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-900"
                      title="Edit session"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(session)}
                      className="rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-900"
                      title="Delete session"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </td>
      </tr>
      {/* Render child sessions if expanded */}
      {canExpand &&
        isExpanded &&
        session.childSessions.map((childSession) => (
          <SessionRow
            key={childSession.id}
            session={childSession}
            isChild={true}
            parentSessionPresent={parentSessionPresent}
            onToggleExpand={onToggleExpand}
            isExpanded={false}
            handleViewDetails={handleViewDetails}
            handleEdit={handleEdit}
            handleSchedule={handleSchedule}
            handleDelete={handleDelete}
            handleReschedule={handleReschedule}
            formatCrewNames={formatCrewNames}
            formatSessionTiming={formatSessionTiming}
            getSessionStatus={getSessionStatus}
            canModify={canModify}
          />
        ))}
    </>
  );
};

export default SessionRow;
